import React, { type CSSProperties, FC, createRef, useState } from 'react'

import { getMimeTypeExtensionMap } from '@common/utils/file'
import AvatarEditor from 'react-avatar-editor'
import Dropzone from 'react-dropzone'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import type { DialogProps } from '@/dialogs/types'
import { useUpdateSettings } from '@/modules/profile-settings/hooks/useSettings.ts'
import { classNames } from '@/utils/helpers.ts'

const deselectedStyles: CSSProperties = { height: 300, width: 300 }

export interface Props extends DialogProps {}

const acceptedFileFormats = getMimeTypeExtensionMap({ types: ['image'] })

export const ImageSelectorDialog: FC<Props> = ({ isOpen, onClose }) => {
  const [selected, setSelected] = useState<string | undefined>()
  const [scale, setScale] = useState<number>(1)
  const editor = createRef<AvatarEditor>()
  const { updatePictureMutation } = useUpdateSettings()

  const onDrop = (acceptedFiles: File[]) => {
    const reader = new FileReader()

    reader.onload = () => {
      const dataURL = reader.result

      setSelected(dataURL as string)
    }
    reader.readAsDataURL(acceptedFiles[0])
  }

  const handleReset = () => {
    setSelected(undefined)
    setScale(1)
  }

  const handleConfirm = async () => {
    if (editor.current) {
      const canvas = editor.current.getImageScaledToCanvas()

      canvas.toBlob((image) => {
        if (image) {
          updatePictureMutation.mutateAsync({ image }).then(() => onClose())
        } else {
          onClose()
        }
      }, 'image/png')
    }
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      testId="profile-settings-image-upload-area"
    >
      <Dialog.Title className="block text-center">
        Select a new Profile Picture
      </Dialog.Title>
      <Dialog.Description className="mb-4 mt-2 block text-center">
        We support PNGs, JPEGs & GIFs under 10MB
      </Dialog.Description>
      <div className="flex flex-col justify-center">
        <Dropzone
          onDrop={onDrop}
          accept={acceptedFileFormats}
        >
          {({ getRootProps, getInputProps }) => (
            <div className="mx-auto inline-flex flex-col gap-4 p-4">
              {selected && (
                <div className="overflow-none bg-black">
                  <AvatarEditor
                    ref={editor}
                    image={selected}
                    width={300}
                    height={300}
                    border={0}
                    color={[255, 255, 255, 0.6]}
                    scale={scale}
                    rotate={0}
                    crossOrigin="anonymous"
                  />
                </div>
              )}
              {!selected && (
                <div
                  style={deselectedStyles}
                  className="group flex cursor-pointer flex-col items-center justify-center rounded-sm border border-secondary-200 bg-secondary-50 text-center transition hover:border-primary-200 hover:bg-primary-50 dark:bg-secondary-600"
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    fill="currentColor"
                    className="h-12 w-12 text-secondary-400 transition group-hover:text-primary-500"
                  >
                    <path
                      fill="none"
                      d="M0 0h24v24H0z"
                    />
                    <path d="M4 19h16v-7h2v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-8h2v7zm9-10v7h-2V9H6l6-6 6 6h-5z" />
                  </svg>
                </div>
              )}
            </div>
          )}
        </Dropzone>
        <div className="flex w-60 flex-col items-center justify-center gap-4 self-center text-black dark:text-white">
          <div className="flex w-full justify-between">
            <p>Zoom</p>
            <p>2.0</p>
          </div>
          <input
            disabled={!selected}
            className={classNames(
              'transparent h-1.5 w-60 cursor-pointer appearance-none rounded-lg border-transparent bg-secondary-200 accent-primary-500',
              !selected && 'cursor-not-allowed opacity-50',
            )}
            type="range"
            min={1}
            max={4}
            step={0.1}
            value={scale}
            onChange={(event) => setScale(parseFloat(event.target.value))}
          />
        </div>
      </div>
      <Dialog.Footer>
        <Button
          type="button"
          variant="outline"
          onClick={handleReset}
          disabled={!selected}
          className="flex flex-1 items-center gap-2"
        >
          Reset
        </Button>
        <Button
          disabled={!selected}
          onClick={handleConfirm}
          className="flex flex-1 items-center gap-2"
        >
          Select
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
